import Vue from "vue";
import { vm } from "@/main";
import * as Ably from 'ably';
import router from "@/router";

const getDefaultState = () => {
	return {
		user: null,
		account: 0,
		currentPlan: {
			next_bill: {
				next_charge_amount: null,
			},
			fastspring: {
				nextChargeDate: null,
			},
			plan_quota: {
				used_storage: null,
				max_storage: null,
			},
			users: {
				added: null,
			}
		},
		channelToken: null,
	};
};

const User = {
	namespaced: true,

	state: getDefaultState(),

	mutations: {
		SET_USER: async (state, user) => {
			state.user = user;
		},

		SET_ACTIVE_ACCOUNT: (state, id) => {
			if (!id) {
				if (localStorage.getItem("activeAccount")) {
					id = parseInt(localStorage.getItem("activeAccount"));
					let account_ids = state.user.accounts.map(account => account.id);
					let account = state.user.accounts.find(account => account.id == id);

					if (!account_ids.includes(id) || account.suspended_at) {
						id = state.user.accounts.find(account => !account.suspended_at).id;
					}
				} else {
					let account = state.user.accounts.find(account => account.plan_id < 7);
					if (account) {
						id = account.id;
					} else {
						id = state.user.accounts.find(account => (account.organization && !account.suspended_at)).id;
					}
				}
			}

			localStorage.setItem("activeAccount", id);
			state.account = state.user.accounts.find(account => account.id == id);
		},

		RESET_STATE: state => {
			Object.assign(state, getDefaultState());
		},

		SET_CURRENT_PLAN: (state, payload) => {
			state.currentPlan = payload;
		},

		SET_CHANNEL_TOKEN: (state, payload) => {
			state.channelToken = payload;
		},

		SET_TERMS_OF_SALE_AGREEMENT: (state, payload) => {
			Vue.set(state.user, 'terms_of_sale_agreement', payload)
		}
	},

	actions: {
		resetState: ({ commit }) => {
			commit("RESET_STATE");
		},

		fetchUser: ({ commit, dispatch }) => {
			return new Promise((resolve, reject) => {
				Vue.prototype.$apiService
					.get("user")
					.then(response => {
						commit("SET_USER", response.data);
						commit("SET_ACTIVE_ACCOUNT");
						resolve(response.data);
					})
					.catch(err => {
						dispatch("resetAll", null, {root: true});
						router.push({ name: "login" });
						window.vm.$snackbar("You have been automatically logged out because your session has expired. Please log back in.", "error");
						reject(err.response);
					});
			});
		},

		updateProfile: async ({ commit, state, dispatch }, payload) => {
			return await Vue.prototype.$apiService
				.patch(`users/${state.user.id}`, payload)
				.then(response => {
					dispatch("fetchUser");
				})
		},

		switchAccount: async ({ commit }, id) => {
			// commit("SET_ACTIVE_ACCOUNT", id);

			if (window.lutify) {
				await window.lutify.storeLastState();
				await window.lutify.pushLastStates();
			}

			localStorage.setItem("activeAccount", id);
			vm.$router.go();
		},

		fetchCurrentPlan: ({ commit, state, getters }) => {
			if (getters.isAdminOrLower) return;

			return Vue.prototype.$apiService.get(`accounts/${state.account.id}/current-plan`).then(response => {
				commit("SET_CURRENT_PLAN", response.data);
				return response;
			});
		},

		fetchChannelToken: ({ commit, state, dispatch }) => {
			return new Promise((resolve, reject) => {
				Vue.prototype.$apiService
					.get(`accounts/${state.account.id}/channel-token`)
					.then(response => {
						commit("SET_CHANNEL_TOKEN", response.data);

						if (Vue.prototype.$ably) Vue.prototype.$ably.close();
						Vue.prototype.$ably = new Ably.Realtime({token: state.channelToken});

						Vue.prototype.$ably.connection.on('suspended', async stateChange => {
							// console.log('[CHANNEL][CONNECTION][CLOSED] fetching new token and connecting');
							await dispatch('subscribeToChannels');
							dispatch('Comments/fetch', null, { root: true });
						});

						Vue.prototype.$ably.connection.on('failed', async stateChange => {
							// console.log('[CHANNEL][CONNECTION][FAILED] fetching new token and connecting');
							await dispatch('subscribeToChannels');
							dispatch('Comments/fetch', null, { root: true });
						});

						resolve(response.data);
					})
					.catch(err => {
						reject(err.response);
					});
			});
		},

		subscribeToChannels: async ({ state, dispatch }) => {
			if (!state.user.accounts.some(account => account.plan_id !== 1)) return;

			await dispatch('fetchChannelToken');

			dispatch('subscribeToChannel');
			dispatch('Organizations/subscribeToChannel', null, { root: true });
			dispatch('Projects/subscribeToChannel', null, { root: true });
		},

		subscribeToChannel: async ({ state, dispatch, rootState }) => {
			if (vm.$root.userChannel) {
				vm.$root.userChannel.unsubscribe();
				vm.$root.userChannel.detach();
			}
			vm.$root.userChannel = Vue.prototype.$ably.channels.get(`user:${state.user.id}`);
			vm.$root.userChannel.subscribe(async message => {
				// console.log(`[CHANNEL][USER] ${message.name}`, message);

				if (message.name == 'PROJECT_DELETED' ||
					message.name == 'USER_REMOVED_FROM_PROJECT' ||
					message.name == 'USER_ADDED_TO_PROJECT') {
					if (message.data.project_id == rootState.Projects.project_id) {
						vm.$router.go();
					} else {
						await dispatch("Organizations/fetchOrganization", null, { root: true });
						await dispatch("Projects/fetchProjects", null, { root: true });
						await dispatch("Projects/setProjectsFeaturedImage", rootState.Projects.projects, { root: true });
						dispatch("Dashboard/resetState", null, { root: true });
					}
				}

				else if (message.name == 'ACCOUNT_SUSPENDED_FROM_ORGANIZATION') {
					if (message.data.account_id == state.account.id) {
						alert('Your account was suspended');
						vm.$router.go();
					} else {
						dispatch('fetchUser');
					}
				}

				else {
					dispatch("Notifications/fetch", null, { root: true });
				}
			});
		},

		updateNotificationSettings: ({ dispatch, state }, payload) => {
			return new Promise((resolve, reject) => {
				Vue.prototype.$apiService
					.patch(`accounts/${state.account.id}/settings/notifications`, payload)
					.then(response => {
						resolve(response.data);
					})
					.catch(err => {
						reject(err.response);
					});
			});
		},

		updateTermsOfSaleAgreement: ({ commit, state }, payload) => {
			// commit('SET_TERMS_OF_SALE_AGREEMENT', payload);
			return Vue.prototype.$apiService
				.patch(`users/${state.user.id}/terms-of-sale-agreement`, {
					value: payload
				});
		}
	},

	getters: {
		activeAccounts: state => {
			return state.user.accounts.filter(account => account.accepted);
		},

		user: state => {
			return state.user;
		},

		isCurrentPlanFree: state => {
			if (state.currentPlan.id == 1) return true;
		},

		isCurrentPlanLowerThanTeam: state => {
			if (state.currentPlan && state.currentPlan.id < 4) return true;
		},

		isLoggedIn: state => {
			if (state.user) return true;
			else return false;
		},

		isOwnerOfProject: state => project => {
			if (!state.user || !project.owner) return false;

			if (project.owner.id == state.user.id) return true;
			else return false;
		},

		isGuest: state => {
			if (state.account.role_id == 7) return true;
			else return false;
		},

		isMemberOrLower: state => {
			if (state.account.role_id >= 6) return true;
			else return false;
		},

		isAdminOrLower: state => {
			if (state.account.role_id >= 3) return true;
			else return false;
		}
	},
};

export default User;
